function assertMethod(value, method, name) {
  if (!value || typeof value[method] !== 'function') {
    throw new TypeError(`${name} must provide ${method}()`);
  }
}

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function objectiveAccepts(objective, event) {
  return objective.status !== 'completed'
    && objective.type === event.type
    && (objective.targetId === null || objective.targetId === event.targetId);
}

export function buildStoryEvents({
  tick,
  simulationTime,
  input,
  huntingStep,
  snakeState,
  storySystem,
  traversalWorld,
  wildlifeSystem,
}) {
  if (!Number.isInteger(tick) || tick < 0) throw new RangeError('tick must be a non-negative integer');
  if (!Number.isFinite(simulationTime) || simulationTime < 0) {
    throw new RangeError('simulationTime must be a non-negative finite number');
  }
  if (!input || typeof input !== 'object') throw new TypeError('input must be an object');
  assertMethod(storySystem, 'getStatus', 'storySystem');
  assertMethod(storySystem, 'getMission', 'storySystem');
  assertMethod(traversalWorld, 'getActorState', 'traversalWorld');
  assertMethod(wildlifeSystem, 'get', 'wildlifeSystem');

  const activeMissionId = storySystem.getStatus().activeMissionId;
  if (activeMissionId === null) return Object.freeze([]);
  const objectives = storySystem.getMission(activeMissionId).state.objectives;
  const candidates = [];

  if (input.actions?.interact?.pressed) {
    candidates.push({
      eventId: `tick-${tick}-interaction-egg-shell`,
      type: 'interaction',
      targetId: 'egg-shell',
      amount: 1,
      tick,
      simulationTime,
      metadata: { source: 'canonical-input' },
    });
  }

  const strike = huntingStep?.strike ?? null;
  if (strike?.outcome === 'kill' && strike.targetEntityId) {
    const record = wildlifeSystem.get(strike.targetEntityId);
    candidates.push({
      eventId: `tick-${tick}-hunt-${strike.targetEntityId}`,
      type: 'hunt',
      targetId: record?.speciesId ?? strike.targetEntityId,
      amount: 1,
      tick,
      simulationTime,
      metadata: {
        source: 'hunting-system',
        targetEntityId: strike.targetEntityId,
      },
    });
  }

  const eat = huntingStep?.eat ?? null;
  if (eat?.outcome === 'consumed' && eat.targetEntityId) {
    candidates.push({
      eventId: `tick-${tick}-eat-${eat.targetEntityId}`,
      type: 'eat',
      targetId: eat.targetEntityId,
      amount: 1,
      tick,
      simulationTime,
      metadata: { source: 'hunting-system' },
    });
    if (Number.isFinite(eat.rewards?.points) && eat.rewards.points > 0) {
      candidates.push({
        eventId: `tick-${tick}-growth-${eat.targetEntityId}`,
        type: 'growth',
        targetId: 'growth-points',
        amount: eat.rewards.points,
        tick,
        simulationTime,
        metadata: { source: 'hunting-system' },
      });
    }
  }

  if (snakeState?.entityId) {
    const traversal = traversalWorld.getActorState(snakeState.entityId);
    if (traversal?.hidden) {
      candidates.push({
        eventId: `tick-${tick}-location-safe-cover`,
        type: 'location-entry',
        targetId: 'safe-cover',
        amount: 1,
        tick,
        simulationTime,
        metadata: {
          source: 'traversal-world',
          actorId: snakeState.entityId,
        },
      });
    }
  }

  return deepFreeze(candidates.filter((event) => (
    objectives.some((objective) => objectiveAccepts(objective, event))
  )));
}
